import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';

// ============================================================================
// API Client Configuration
// ============================================================================

class ApiClient {
  private client: AxiosInstance;
  private readonly baseURL: string;

  constructor() {
    this.baseURL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    this.client.interceptors.request.use(this.handleRequest, this.handleRequestError);
    this.client.interceptors.response.use(this.handleResponse, this.handleResponseError);
  }

  private handleRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = localStorage.getItem('token');

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
      if (import.meta.env.DEV) {
        console.log('[API] Attaching token:', token.substring(0, 10) + '...');
      }
    } else {
      if (import.meta.env.DEV) {
        console.log('[API] No token found in localStorage');
      }
    }

    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data,
      });
    }

    return config;
  };

  private handleRequestError = (error: AxiosError): Promise<AxiosError> => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  };

  private handleResponse = (response: AxiosResponse): AxiosResponse => {
    if (import.meta.env.DEV) {
      console.log(
        `[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`,
        {
          status: response.status,
          data: response.data,
        },
      );
    }
    return response;
  };

  private isRefreshing = false;

  private failedQueue: any[] = [];

  private processQueue = (error: any, token: string | null = null) => {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });

    this.failedQueue = [];
  };

  private handleResponseError = async (error: AxiosError): Promise<any> => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;

    if (status === 401 && !originalRequest._retry) {
      if (this.isRefreshing) {
        return new Promise(function (resolve, reject) {
          apiClient.failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers['Authorization'] = 'Bearer ' + token;
            }
            return axios(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      this.isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token');

      if (!refreshToken) {
        this.logoutUser();
        return Promise.reject(error);
      }

      try {
        const CLIENT_ID = 'nepservice';
        const CLIENT_SECRET = 'nepservicesecret';
        const BASIC_AUTH = btoa(`${CLIENT_ID}:${CLIENT_SECRET}`);

        const params = new URLSearchParams();
        params.append('grant_type', 'refresh_token');
        params.append('refresh_token', refreshToken);

        const response = await axios.post(`${this.baseURL}/oauth/token`, params.toString(), {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Authorization: `Basic ${BASIC_AUTH}`,
          },
        });

        if (response.status === 200) {
          const { access_token, refresh_token } = response.data;

          localStorage.setItem('token', access_token);
          if (refresh_token) {
            localStorage.setItem('refresh_token', refresh_token);
          }

          if (originalRequest.headers) {
            originalRequest.headers['Authorization'] = 'Bearer ' + access_token;
          }

          this.processQueue(null, access_token);
          this.isRefreshing = false;

          return axios(originalRequest);
        }
      } catch (err) {
        this.processQueue(err, null);
        this.isRefreshing = false;
        this.logoutUser();
        return Promise.reject(err);
      }
    }

    if (status === 401 && originalRequest._retry) {
      this.logoutUser();
      return Promise.reject(error);
    }

    if (status === 403) {
      console.warn('[API] Forbidden - insufficient permissions');
    }

    if (status === 404) {
      console.warn('[API] Resource not found');
    }

    if (status && status >= 500) {
      console.error('[API] Server error');
    }

    if (import.meta.env.DEV) {
      console.error('[API Response Error]', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        data: error.response?.data,
      });
    }

    return Promise.reject(error);
  };

  private logoutUser() {
    console.warn('[API] Session expired or invalid - logging out');
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    window.location.href = '/login';
  }

  public get<T>(url: string, config?: InternalAxiosRequestConfig): Promise<T> {
    return this.client.get<T>(url, config).then((response) => response.data);
  }

  public post<T>(url: string, data?: unknown, config?: InternalAxiosRequestConfig): Promise<T> {
    return this.client.post<T>(url, data, config).then((response) => response.data);
  }

  public put<T>(url: string, data?: unknown, config?: InternalAxiosRequestConfig): Promise<T> {
    return this.client.put<T>(url, data, config).then((response) => response.data);
  }

  public patch<T>(url: string, data?: unknown, config?: InternalAxiosRequestConfig): Promise<T> {
    return this.client.patch<T>(url, data, config).then((response) => response.data);
  }

  public delete<T>(url: string, config?: InternalAxiosRequestConfig): Promise<T> {
    return this.client.delete<T>(url, config).then((response) => response.data);
  }

  public getBaseURL(): string {
    return this.baseURL;
  }
}

export const apiClient = new ApiClient();
export default apiClient;
